import React, { useLayoutEffect, useRef, useState } from "react";
import AnalogClock from "./AnalogClock";
import DigitalClock from "./DigitalClock";
import { CARD_BORDER, HOVER_ROOM, computeTimeMatchLayout } from "./timeMatchLayout";

// Used until the board has been measured (and in jsdom, which has no layout).
const FALLBACK_BOX = { width: 800, height: 480 };

/**
 * The space the board may fill: the game area's width x the viewport height
 * left below the board's top edge, after the layout's bottom padding and the
 * footer. Also returns the height without the footer, for when the footer has
 * to drop below the fold. None of these inputs depend on the board's own size,
 * so sizing the board from them cannot feed back into the measurement.
 * Re-measured on window resize and whenever the info card or the game area
 * changes size (a wrapping title moves the board down), rAF-debounced.
 */
function useAvailableBox(ref) {
  const [box, setBox] = useState(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const layout = el.closest(".game-layout-topbar");

    const measure = () => {
      const width = el.clientWidth;
      if (!width) return;
      const top = el.getBoundingClientRect().top + window.scrollY;
      const padBottom = layout
        ? parseFloat(getComputedStyle(layout).paddingBottom) || 0
        : 0;
      const footer = document.querySelector(".footer-container");
      const heightNoFooter = Math.floor(window.innerHeight - top - padBottom);
      const height =
        heightNoFooter -
        (footer ? Math.ceil(footer.getBoundingClientRect().height) : 0);
      setBox((prev) =>
        prev &&
        prev.width === width &&
        prev.height === height &&
        prev.heightNoFooter === heightNoFooter
          ? prev
          : { width, height, heightNoFooter }
      );
    };

    let raf = 0;
    const schedule = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(measure);
    };

    measure();
    window.addEventListener("resize", schedule);
    let observer = null;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(schedule);
      observer.observe(el);
      const info = layout && layout.querySelector(".game-layout-topbar__info");
      if (info) observer.observe(info);
    }
    return () => {
      window.removeEventListener("resize", schedule);
      if (observer) observer.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [ref]);

  return box;
}

/**
 * TimeMatchBoard — the play area shared by both Time Match games: one question
 * clock and a grid of answer buttons, sized so the whole board fits on screen
 * without scrolling (see timeMatchLayout.js). On short, wide screens the
 * question sits beside the answers; elsewhere it sits above them.
 *
 * @param {"analog"|"digital"} questionKind - the answers are the other kind
 * @param {Date} questionTime
 * @param {Date[]} optionTimes
 * @param {(boolean|null)[]} checks - per answer: true/false once picked
 * @param {(index: number) => void} onSelect
 * @param {boolean} showSeconds
 * @param {boolean} hour24
 */
function TimeMatchBoard({
  questionKind,
  questionTime,
  optionTimes,
  checks,
  onSelect,
  showSeconds,
  hour24,
}) {
  const ref = useRef(null);
  const box = useAvailableBox(ref);

  const params = {
    count: optionTimes.length,
    questionKind,
    showSeconds,
    hour24,
  };
  const measured = box || FALLBACK_BOX;
  let layout = computeTimeMatchLayout({ ...params, ...measured });
  let boardHeight = measured.height;
  if (!layout.fits && box) {
    // Let the footer drop below the fold before giving up and scrolling.
    const withoutFooter = computeTimeMatchLayout({
      ...params,
      width: box.width,
      height: box.heightNoFooter,
    });
    if (withoutFooter.fits) {
      layout = withoutFooter;
      boardHeight = box.heightNoFooter;
    }
  }

  const { arrangement, spacing, gridWidth, question, option } = layout;

  const renderClock = (clock, time) =>
    clock.kind === "analog" ? (
      <AnalogClock initialTime={time} size={clock.size} showSeconds={showSeconds} />
    ) : (
      <DigitalClock
        initialTime={time}
        hour24={hour24}
        showSeconds={showSeconds}
        fontSize={clock.size}
        width={clock.w}
        height={clock.h}
      />
    );

  return (
    // min-height: the board spans the space it was given, so a board that hit
    // its size caps sits centred in it instead of hugging the info card.
    <div
      ref={ref}
      className="time-match-board"
      style={box ? { minHeight: Math.max(0, boardHeight) } : undefined}
    >
      <div
        className={`time-match-board__layout time-match-board__layout--${arrangement}`}
        style={{ gap: spacing.sectionGap }}
      >
        <div className="time-match-question">
          {renderClock(question, questionTime)}
        </div>
        <div
          className="time-match-choices"
          style={{
            width: gridWidth,
            gap: spacing.gridGap,
            padding: `${HOVER_ROOM / 2}px 0`,
          }}
        >
          {optionTimes.map((time, index) => (
            <button
              type="button"
              key={index}
              className={`clock-option${
                checks[index] === true
                  ? " clock-option--correct"
                  : checks[index] === false
                  ? " clock-option--incorrect"
                  : ""
              }`}
              style={{ padding: spacing.cardPad, borderWidth: CARD_BORDER }}
              onClick={() => onSelect(index)}
            >
              {renderClock(option, time)}
              {checks[index] !== null && checks[index] !== undefined && (
                <div className="clock-option__feedback">
                  {checks[index] ? "✅" : "❌"}
                </div>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default TimeMatchBoard;
